/**
 * Tipos e chamadas da importação de nota fiscal de compra (XML da NF-e).
 *
 * O contrato espelha `api/internal/modulos/notasfiscais/dominio.go`. Três
 * pontos carregam a maior parte do comportamento das telas:
 *
 * 1. A identidade que atravessa a fronteira é a própria CHAVE DE ACESSO (44
 *    dígitos) — não um código sequencial e não o uuid (CONSTITUICAO.md §2). É
 *    a mesma chave impressa no DANFE, e é ela que compõe a URL:
 *    `/notas-fiscais/{chave}`.
 * 2. Todo item chega com um bloco `conciliacao`, nunca com um `sku_id` cru: a
 *    conciliação automática por EAN já aconteceu no servidor quando possível,
 *    e o que sobra pendente é decisão humana — a tela nunca inventa o
 *    casamento de um item com um SKU.
 * 3. Importar tem DUAS etapas, e elas não são a mesma chamada: `analisar` lê o
 *    XML e não grava nada (é o que alimenta a revisão), `importar` grava o que
 *    a revisão aprovou. Quem chamar só a segunda pula a conferência — e essa é
 *    justamente a etapa que existe porque criar fornecedor, gravar documento
 *    fiscal e somar saldo não se desfazem com um botão.
 */
import { api } from './api'
import type { Centavos } from './dinheiro'

export type StatusNota = 'importada' | 'cancelada'
export type StatusConciliacao = 'pendente' | 'conciliado' | 'lancado'

/** O que a nota já produziu de entrada no estoque. Deriva dos itens. */
export type SituacaoEstoque = 'a_lancar' | 'parcial' | 'lancada' | 'cancelada'

/** O KPI clicado na listagem. */
export type Recorte = 'pendentes' | 'a_lancar' | 'sem_ean'

export type FornecedorResumo = {
  codigo: string
  nome: string
  /**
   * CNPJ (ou CPF) só dígitos. Vem porque a ficha da nota é conferida CONTRA O
   * DANFE, e quem confere lê o documento: dois fornecedores da mesma rede têm
   * nome quase igual e documento diferente. Ausente quando o cadastro não tem.
   */
  documento?: string
}

export type SKUResumo = {
  codigo_barras: string
  produto: string
  cor: string
  tamanho: string
}

export type Conciliacao = {
  status: StatusConciliacao
  status_rotulo: string
  /** Ausente enquanto o item está pendente. */
  sku?: SKUResumo
}

export type ItemNota = {
  numero_item: number
  codigo_fornecedor: string
  ean?: string
  descricao: string
  ncm?: string
  cfop?: string
  quantidade: number
  valor_unitario_centavos: Centavos
  valor_total_centavos: Centavos
  valor_ibs_centavos: Centavos
  valor_cbs_centavos: Centavos
  conciliacao: Conciliacao
}

export type FichaNota = {
  chave_acesso: string
  numero: string
  serie: string
  natureza_operacao?: string
  emitida_em: string
  fornecedor: FornecedorResumo
  valor_produtos_centavos: Centavos
  valor_ibs_centavos: Centavos
  valor_cbs_centavos: Centavos
  // Bloco clássico (ICMSTot) — ausente (`omitempty`) quando zero, o comum
  // para quem só emite pela regra nova da reforma tributária.
  valor_icms_centavos?: Centavos
  valor_ipi_centavos?: Centavos
  valor_pis_centavos?: Centavos
  valor_cofins_centavos?: Centavos
  valor_frete_centavos?: Centavos
  valor_seguro_centavos?: Centavos
  valor_desconto_centavos?: Centavos
  valor_outras_despesas_centavos?: Centavos
  valor_total_centavos: Centavos
  status: StatusNota
  status_rotulo: string
  importada_em: string
  itens: ItemNota[]
  itens_pendentes: number
}

// ------------------------------------------------------------------ listagem

export type LinhaNota = {
  chave_acesso: string
  numero: string
  serie: string
  natureza_operacao?: string
  fornecedor: FornecedorResumo
  emitida_em: string
  importada_em: string
  valor_total_centavos: Centavos
  status: StatusNota
  status_rotulo: string
  itens: number
  itens_pendentes: number
  itens_lancados: number
  itens_sem_ean: number
  situacao_estoque: SituacaoEstoque
  situacao_estoque_rotulo: string
  valor_a_lancar_centavos: Centavos
  valor_lancado_centavos: Centavos
}

/**
 * Os números dos KPIs e dos chips.
 *
 * Vêm do servidor IGNORANDO o chip e o recorte escolhidos — só o período, o
 * fornecedor e a busca contam. É isso que faz o chip não clicado continuar
 * mostrando quantas notas existem nele, em vez de zerar a cada clique.
 */
export type ContagensDeNotas = {
  total: number
  a_lancar: number
  parcial: number
  lancada: number
  cancelada: number
  notas_com_pendencia: number
  itens_pendentes: number
  notas_a_lancar: number
  valor_a_lancar_centavos: Centavos
  notas_sem_ean: number
  itens_sem_ean: number
}

/** O pé da tabela: soma o recorte inteiro, não a página. Canceladas fora. */
export type SomasDeNotas = {
  total_centavos: Centavos
  lancado_centavos: Centavos
  a_lancar_centavos: Centavos
  pendente_centavos: Centavos
}

export type ListaDeNotas = {
  linhas: LinhaNota[]
  total: number
  pagina: number
  tamanho: number
  paginas: number
  contagens: ContagensDeNotas
  somas: SomasDeNotas
}

export type FiltroDeNotas = {
  /** `2026-09-01` — data local, inclusiva. */
  de?: string
  /** `2026-10-01` — data local, EXCLUSIVA (o servidor compara com `<`). */
  ate?: string
  fornecedor?: string
  situacao?: SituacaoEstoque | ''
  recorte?: Recorte | ''
  busca?: string
  pagina?: number
  tamanho?: number
}

// -------------------------------------------------------------------- prévia

export type EnderecoEmitente = {
  logradouro?: string
  numero?: string
  bairro?: string
  cidade?: string
  uf?: string
  cep?: string
}

export type EmitentePrevia = {
  documento: string
  eh_pessoa_juridica: boolean
  nome: string
  inscricao_estadual?: string
  endereco: EnderecoEmitente
}

export type FornecedorPrevia = {
  cadastrado: boolean
  codigo?: string
  nome?: string
}

export type FilialResumo = {
  codigo: string
  nome: string
}

export type ItemPrevia = {
  numero_item: number
  codigo_fornecedor: string
  ean?: string
  descricao: string
  ncm?: string
  cfop?: string
  quantidade: number
  valor_unitario_centavos: Centavos
  valor_total_centavos: Centavos
  valor_ibs_centavos: Centavos
  valor_cbs_centavos: Centavos
  /** Só vem quando o EAN do item bate exatamente com um SKU ativo da loja. */
  sku_sugerido?: SKUResumo
}

export type PreviaNota = {
  chave_acesso: string
  numero: string
  serie: string
  natureza_operacao?: string
  emitida_em: string
  emitente: EmitentePrevia
  fornecedor: FornecedorPrevia
  filial_destino: FilialResumo
  itens: ItemPrevia[]
  itens_sugeridos: number
  itens_pendentes: number
  valor_produtos_centavos: Centavos
  valor_ibs_centavos: Centavos
  valor_cbs_centavos: Centavos
  valor_icms_centavos?: Centavos
  valor_ipi_centavos?: Centavos
  valor_pis_centavos?: Centavos
  valor_cofins_centavos?: Centavos
  valor_frete_centavos?: Centavos
  valor_seguro_centavos?: Centavos
  valor_desconto_centavos?: Centavos
  valor_outras_despesas_centavos?: Centavos
  valor_total_centavos: Centavos
  ja_importada: boolean
  importada_em?: string
}

export type ConciliacaoEscolhida = {
  numero_item: number
  sku: string
}

export type PedidoDeImportacao = {
  xml: string
  conciliacoes: ConciliacaoEscolhida[]
  lancar_estoque: boolean
}

export type ResultadoImportacao = {
  nota: FichaNota
  estoque_lancado: boolean
  itens_lancados: number[]
  itens_pendentes: number[]
}

export type ResultadoLancamento = {
  itens_lancados: number[]
  itens_ja_lancados_antes: number[]
  itens_pendentes: number[]
  nota: FichaNota
}

// ------------------------------------------------------------------ chamadas

/**
 * Lê o XML e devolve a prévia — NADA é gravado nesta chamada.
 *
 * `Content-Type: application/xml`: o corpo é o próprio arquivo, sem envelope
 * multipart — a NF-e não tem anexo, ela É o anexo (ver o handler no backend).
 */
export function analisarNotaFiscal(xml: string, sinal?: AbortSignal): Promise<PreviaNota> {
  return api.postArquivo<PreviaNota>('/notas-fiscais/analisar', xml, 'application/xml', sinal)
}

/**
 * Grava o que a revisão aprovou.
 *
 * Um 409 aqui é a nota já ter sido importada entre a análise e a conclusão; a
 * tela trata esse código à parte (ver `ErroApi.codigo === 'conflito'`), com a
 * mensagem que a API já manda pronta ("Esta nota já foi importada em ..."). Um
 * 422 aponta o ITEM cujo código de barras não existe, em `campos.item_N`, e
 * nada foi gravado.
 */
export function importarNotaFiscal(pedido: PedidoDeImportacao): Promise<ResultadoImportacao> {
  return api.post<ResultadoImportacao>('/notas-fiscais/importar', pedido)
}

export function carregarNotasFiscais(
  filtro: FiltroDeNotas = {},
  sinal?: AbortSignal,
): Promise<ListaDeNotas> {
  const params = new URLSearchParams()
  if (filtro.de) params.set('de', filtro.de)
  if (filtro.ate) params.set('ate', filtro.ate)
  if (filtro.fornecedor) params.set('fornecedor', filtro.fornecedor)
  if (filtro.situacao) params.set('situacao', filtro.situacao)
  if (filtro.recorte) params.set('recorte', filtro.recorte)
  if (filtro.busca) params.set('busca', filtro.busca)
  if (filtro.pagina && filtro.pagina > 1) params.set('pagina', String(filtro.pagina))
  if (filtro.tamanho) params.set('tamanho', String(filtro.tamanho))

  const consulta = params.toString()
  return api.get<ListaDeNotas>(`/notas-fiscais${consulta ? `?${consulta}` : ''}`, sinal)
}

export function carregarFichaNotaFiscal(
  chave: string,
  sinal?: AbortSignal,
): Promise<FichaNota> {
  return api.get<FichaNota>(`/notas-fiscais/${encodeURIComponent(chave)}`, sinal)
}

/**
 * Concilia manualmente um item com um SKU da loja, numa nota JÁ IMPORTADA.
 *
 * `sku` é o CÓDIGO DE BARRAS — a mesma identidade que
 * `estoque.ts`/`EntradaAjuste` já usa para o leitor bipar. Não existe "código
 * de SKU" separado do código de barras neste sistema.
 */
export function conciliarItem(
  chave: string,
  numeroItem: number,
  codigoBarras: string,
): Promise<FichaNota> {
  return api.put<FichaNota>(
    `/notas-fiscais/${encodeURIComponent(chave)}/itens/${numeroItem}`,
    { sku: codigoBarras },
  )
}

/**
 * Dá entrada em estoque para todos os itens já conciliados. Itens sem SKU são
 * pulados e voltam em `itens_pendentes` — chamar de novo não duplica o que já
 * foi lançado (`itens_ja_lancados_antes`).
 */
export function lancarNoEstoque(chave: string): Promise<ResultadoLancamento> {
  return api.post<ResultadoLancamento>(`/notas-fiscais/${encodeURIComponent(chave)}/lancar`, {})
}

/** Quantos itens ainda não têm nenhuma decisão (nem SKU, nem "pular"). */
export function contarSemDecisao(
  itens: ItemNota[],
  decisoes: Partial<Record<number, 'pular'>>,
): number {
  return itens.filter(
    (item) => item.conciliacao.status === 'pendente' && decisoes[item.numero_item] !== 'pular',
  ).length
}

/**
 * Os meses que o seletor da listagem oferece: o atual e os onze anteriores.
 *
 * Mora aqui, e não na tela, porque o período é parte do contrato do filtro —
 * `de` inclusivo, `ate` exclusivo — e errar essa borda por um dia é o tipo de
 * bug que só aparece na virada do mês.
 */
export function mesesRecentes(hoje = new Date()): { valor: string; rotulo: string }[] {
  return Array.from({ length: 12 }, (_, i) => {
    const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1)
    const mes = String(data.getMonth() + 1).padStart(2, '0')
    return {
      valor: `${data.getFullYear()}-${mes}`,
      rotulo: `${data.toLocaleDateString('pt-BR', { month: 'long' })} de ${data.getFullYear()}`,
    }
  })
}

/** `2026-09` vira o intervalo `[2026-09-01, 2026-10-01)`. */
export function intervaloDoMes(mes: string): { de: string; ate: string } {
  const [ano, numero] = mes.split('-').map(Number)
  // `numero` já é o mês seguinte na contagem do Date (que começa em zero):
  // "2026-09" → new Date(2026, 9, 1) é 1º de outubro, o fim exclusivo.
  const proximo = new Date(ano ?? 0, numero ?? 0, 1)
  const mesSeguinte = String(proximo.getMonth() + 1).padStart(2, '0')
  return {
    de: `${mes}-01`,
    ate: `${proximo.getFullYear()}-${mesSeguinte}-01`,
  }
}
